import React, { useEffect, useState } from "react";
import {
  Shield, ShieldAlert, Anchor, KeyRound, Brain, Waves, Radio,
  Heart, HeartPulse, Atom, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { layersStatus } from "../lib/api";
import {
  LAYER_GLYPHS, SUPERVISORY_GLYPHS, OPERATIONAL_TOKENS,
  ORION_EXPANSION_TOKENS, REGISTRY_VERSION, layerGlyph,
} from "../lib/glyphs";

const ICON_BY_ID = {
  0: ShieldAlert,
  1: Anchor,
  2: KeyRound,
  3: Brain,
  4: Waves,
  5: Radio,
  6: Atom,
  7: HeartPulse,
};

const ROLE_COLOR = {
  core: "border-amber-500 text-amber-400 bg-amber-500/5",
  inbound: "border-sky-500/60 text-sky-300 bg-sky-500/5",
  outbound: "border-emerald-500/60 text-emerald-300 bg-emerald-500/5",
};

const STATUS_COLOR = {
  ARMED: "text-emerald-400",
  SAFE_MODE: "text-red-400",
  UNCONFIGURED: "text-zinc-500",
};

export default function Shield7() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      setData(await layersStatus());
    } catch {
      toast.error("Failed to load Sentinel shield");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="flex items-start justify-between gap-4 mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 flex items-center gap-2">
            <Shield className="h-3 w-3" /> Aegis Enclosure · {data?.version || "v18.48"}
          </div>
          <h1 className="font-heading text-4xl mt-1">7-Layer Security Shield</h1>
          <div className="text-xs text-zinc-500 mt-2 max-w-2xl leading-relaxed">
            Layer 0 is the soul · Layers 1-3 keep the world from confusing the Sentinel ·
            Layers 4-7 keep the Sentinel from confusing the world.
          </div>
        </div>
        <button
          onClick={load}
          data-testid="shield-refresh-btn"
          className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-700 text-xs uppercase tracking-[0.3em] hover:border-emerald-500 hover:text-emerald-400 transition-colors"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {loading && !data ? (
        <div className="border border-zinc-800 bg-[#0b0b0d] p-12 text-center text-xs uppercase tracking-[0.3em] text-zinc-500">
          Scanning layers…
        </div>
      ) : (
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
          data-testid="layers-grid"
        >
          {(data?.layers || []).map((layer) => {
            const Icon = ICON_BY_ID[layer.id] || Heart;
            const roleClass = ROLE_COLOR[layer.role] || "";
            const statusClass = STATUS_COLOR[layer.status] || "text-zinc-300";
            return (
              <div
                key={layer.id}
                data-testid={`layer-tile-${layer.id}`}
                className={`border ${roleClass} bg-[#0b0b0d] p-5 flex flex-col gap-3 relative overflow-hidden`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">
                    <span className="text-zinc-300 mr-1.5">{layerGlyph(layer.id) || "·"}</span>
                    L{layer.id}
                  </span>
                  <span className={`text-[10px] uppercase tracking-[0.3em] ${statusClass}`}>
                    ● {layer.status}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Icon className="h-5 w-5 shrink-0" />
                  <div className="font-heading text-lg leading-tight">
                    {layer.name}
                  </div>
                </div>
                <div className="text-[11px] text-zinc-400 leading-relaxed min-h-[40px]">
                  {layer.description}
                </div>
                <div className="mt-auto pt-2 border-t border-zinc-800/80 flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  <span>· {layer.role}</span>
                  {layer.violations !== undefined && (
                    <span>· violations: {layer.violations}</span>
                  )}
                  {layer.anchor_chunks !== undefined && (
                    <span>· chunks: {layer.anchor_chunks}</span>
                  )}
                  {layer.archive_docs !== undefined && (
                    <span>· docs: {layer.archive_docs}</span>
                  )}
                  {layer.strictness && (
                    <span>· {layer.strictness}</span>
                  )}
                  {layer.anchor_trips !== undefined && (
                    <span>· trips: {layer.anchor_trips}</span>
                  )}
                  {layer.truth_drifts !== undefined && (
                    <span>· truth: {layer.truth_drifts}</span>
                  )}
                  {layer.drift_neutralizations !== undefined && (
                    <span>· drift: {layer.drift_neutralizations}</span>
                  )}
                  {layer.total_pulses !== undefined && (
                    <span>· pulses: {layer.total_pulses}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Symbolic Registry · canonical glyph legend ─────────────── */}
      <section className="mt-16" data-testid="symbolic-registry">
        <div className="flex items-end justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.35em] text-emerald-400">
              Symbolic Registry · {REGISTRY_VERSION}
            </div>
            <h2 className="font-heading text-2xl mt-1">Operator Shorthand</h2>
            <p className="text-xs text-zinc-500 mt-1 max-w-2xl leading-relaxed">
              Every layer, supervisor and token carries a unique glyph. The
              manifest hash commits each signed receipt to this exact registry,
              so the shorthand is unforgeable: ▲ L0 always means Aegis.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Numbered layers (full L0–L10) */}
          <div className="border border-zinc-800 bg-[#0b0b0d] p-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
              Numbered layers · L0 → L10
            </div>
            <ul className="space-y-1.5">
              {Object.entries(LAYER_GLYPHS).map(([n, m]) => (
                <li key={n} className="flex items-baseline gap-3 text-[12px]">
                  <span className="font-mono text-zinc-200 w-20 shrink-0">{m.label}</span>
                  <span className="text-zinc-400">{m.short}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Supervisory components */}
          <div className="border border-zinc-800 bg-[#0b0b0d] p-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
              Supervisory components
            </div>
            <ul className="space-y-1.5">
              {Object.entries(SUPERVISORY_GLYPHS).map(([name, m]) => (
                <li key={name} className="flex items-baseline gap-3 text-[12px]">
                  <span className="font-mono text-zinc-200 w-24 shrink-0">{m.glyph} {m.token}</span>
                  <span className="text-zinc-400">{name}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Operational tokens (verbatim from phone) */}
          <div className="border border-zinc-800 bg-[#0b0b0d] p-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
              Operational tokens
            </div>
            <ul className="space-y-1.5">
              {OPERATIONAL_TOKENS.map((t) => (
                <li key={t.token} className="flex items-baseline gap-3 text-[12px]">
                  <span className="font-mono text-zinc-200 w-24 shrink-0">{t.glyph} {t.token}</span>
                  <span className="text-zinc-400">{t.summary}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Orion expansion (some reserved for paused crypto/NFT spike) */}
          <div className="border border-zinc-800 bg-[#0b0b0d] p-4 lg:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
              Orion expansion · reserved namespace
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
              {ORION_EXPANSION_TOKENS.map((t) => (
                <li key={t.token} className="flex items-baseline gap-3 text-[12px]">
                  <span className="font-mono text-zinc-200 w-24 shrink-0">{t.glyph} {t.token}</span>
                  <span className="text-zinc-400 flex-1">{t.summary}</span>
                  <span className={`text-[10px] uppercase tracking-[0.2em] ${t.status?.startsWith("live") ? "text-emerald-400" : "text-zinc-600"}`}>
                    {t.status}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
